import { CreateTagRequest } from '@app/models/requests';
import { TagInterface } from '@app/models/util';
import { getCustomers } from '@app/services/customers';
import { createTag, getPaginatedTags } from '@app/services/tag';
import { createTagValidation } from '@app/utils/validations';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

export const useTagsPage = () => {
  const [addTagOpen, setAddTagOpen] = useState(false);
  const [tagQuery, setTagQuery] = useState({ page: 1, limit: 10 });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  // Row menu deletes one tag without changing the checkbox selection
  const [deleteIds, setDeleteIds] = useState<string[] | null>(null);
  const createTagForm = useForm({ resolver: createTagValidation });

  const {
    data: tagsData,
    isLoading,
    refetch,
  } = useQuery<{ tags: TagInterface[]; count: number }>({
    queryKey: ['tags-paginated', tagQuery.page, tagQuery.limit, sortBy, sortOrder],
    queryFn: () => getPaginatedTags({ page: tagQuery.page, perPage: tagQuery.limit, sortBy, sortOrder }).then((res) => res.data),
  });

  const { data: customers } = useQuery({
    queryKey: ['customers-all'],
    queryFn: () => getCustomers(),
  });

  const handleAddTag = () => {
    setAddTagOpen(!addTagOpen);
    if (!addTagOpen) createTagForm.reset();
  };

  const { mutateAsync: addTag, isPending: isPendingTag } = useMutation({
    mutationKey: ['add-tag'],
    mutationFn: (data: CreateTagRequest) => createTag(data),
    onSuccess: () => {
      refetch();
      handleAddTag();
    },
    onError: (err) => {
      toast.error('Error creating tag');
      console.error(err);
    },
  });

  const submitTag = async (values: FieldValues) => {
    await addTag(values as CreateTagRequest);
  };

  const handleSelectedTags = (id: string | undefined) => {
    if (!id) return;

    if (id === 'all') {
      const pageIds = tagsData?.tags.map((tag) => tag.id) ?? [];
      setSelectedTags(selectedTags.length === pageIds.length ? [] : pageIds);
      return;
    }

    setSelectedTags((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const handleNextPage = () => {
    setSelectedTags([]);
    setTagQuery((prev) => ({ ...prev, page: prev.page + 1 }));
  };

  const handlePrevPage = () => {
    setSelectedTags([]);
    setTagQuery((prev) => ({ ...prev, page: Math.max(prev.page - 1, 1) }));
  };

  const handleLimit = (limit: number) => {
    setSelectedTags([]);
    setTagQuery({ page: 1, limit });
  };

  const handleSortingTable = (field: string) => {
    setSelectedTags([]);
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleDeleted = () => {
    setSelectedTags([]);
    if (tagQuery.page === 1) {
      refetch();
    } else {
      setTagQuery((prev) => ({ ...prev, page: 1 }));
    }
  };

  const deletingTags = (tagsData?.tags ?? []).filter((tag) => deleteIds?.includes(tag.id));

  return {
    addTagOpen,
    handleAddTag,
    createTagForm,
    submitTag,
    isPendingTag,
    tagsData,
    isLoading,
    customers,
    tagQuery,
    selectedTags,
    handleSelectedTags,
    handleNextPage,
    handlePrevPage,
    handleLimit,
    sortBy,
    sortOrder,
    handleSortingTable,
    deleteIds,
    setDeleteIds,
    deletingTags,
    handleDeleted,
  };
};
